
import util from '../lib/util';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";
import {ValidationError} from "../lib/errors";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}
 /**
 * Participant
 *
 * A participant record links a contact to an owning entity (a matter, lead, or contact) and describes their role via one or more relationship labels (e.g. Client, Spouse, Brother-in-Law). The Client relationship is sacrosanct and is used throughout the app to identify who the firm represents; familial relationships carry reciprocal-opposite metadata so that both sides of a relationship can be recorded automatically.
 */
class Participant extends Base implements EntityType {
    participantId: uuid;
    contactId: uuid;
    relationships: uuid[];
    description?: string | null;

    /**
     * Create a new Participant, with an (optional) JSON object or Participant class
     *
     * @param {Object|Participant} doc - Can be either a simple JSON object or a Participant class used to initialize this object's fields
     * @constructor
     */
    constructor(doc: typeof Participant | any) {
        super(doc);
        
        // @ts-ignore
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: 'C',
            entityId: null,
            entityType: null,
            participantId: genUUID(),
            contactId: null,
            relationships: [],
        };
        
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || 'participant';
        // @ts-ignore
        this.participantId = doc._participantId || doc.participantId || doc.participantid || genUUID();
        // @ts-ignore
        this.contactId = doc._contactId || doc.contactId || doc.contactid || null;
        // @ts-ignore
        this.relationships = doc._relationships || doc.relationships || [];
        // @ts-ignore
        this.description = doc._description || doc.description || null;

    }
    /**
     * @returns {uuid} This is a generic pointer to the participantId across all entity types
     */
    get id(): uuid {
        return this.participantId;
    }


    /**
     * Add a relationship to participant.relationships
     *
     * @param {uuid} relationship - The Relationship to add
     * @returns uuid - The relationship with any defaulted fields set
     */
    addRelationship(relationship: uuid) : uuid {
        if (!relationship) throw new ValidationError('No nulls allowed in an array!');
    
        if (!this.relationships) this.relationships = [];
        this.relationships.push(relationship);

        return relationship;
    }

    /**
     * Update a relationship from participant
     *
     * @param {uuid} relationship - The Relationship to remove
     */
    updateRelationship(relationship: uuid) {
        if (!this.relationships) this.relationships = [];
        
        // Find the matching object by relationship.relationshipId
        const index = this.relationships.findIndex(item => item === relationship);
        if (index !== -1) {
            this.relationships[index] = relationship;
        }
        else this.addRelationship(relationship);
    }

    /**
     * Remove a relationship from participant
     *
     * @param {uuid} relationship - The Relationship to remove
     */
    removeRelationship(relationship: uuid) {
        if (!this.relationships) return;
        
        // Find the matching object by relationship.relationshipId
        let foundItems = this.relationships?.filter(item => item === relationship);
        if (foundItems && foundItems.length > 0) {
            // Always remove it from the array
            this.relationships = this.relationships.filter(item => !(item === relationship));
        }
    }

    /**
     * Abandon all elements. This is used when transferring ref fields to another owner.
     */
     abandonRelationships() {
        this.relationships = [];
     }
        
    /**
     * Get a list of pk fields (primary keys)
     *
     * @returns {SchemaField[]}
     */
    static override getPkFields(): SchemaField[] {
        return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique id for this participant"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}];
    }
    /**
     * TODO: Added just to handle react-select issues.  Can remove when remedied.
     */
    toLowerCase() {
        return this.participantId;
    }
    
    /**
     * Get a list of non-reference array fields in the schema
     *
     * @return {Array.<{field: string, entity: string}>}
     */
    static override getNonRefArrayFields(): string[] {
        return ["relationships"];
    }

    /**
     * Default entries are Outlaw-supplied Participant entries
     * @static
     * @return {object[]}
     */
    static override getDefaultEntries(): Participant[] {
        return [];
    }

    /**
     * Get a reference to the schema entity.
     * @static
     * @return {object} - the original schema
     */
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
            entity: 'participant',
            table: 'PARTICIPANT',
            comment: 'A participant record links a contact to an owning entity (a matter, lead, or contact) and describes their role via one or more relationship labels (e.g. Client, Spouse, Brother-in-Law). The Client relationship is sacrosanct and is used throughout the app to identify who the firm represents; familial relationships carry reciprocal-opposite metadata so that both sides of a relationship can be recorded automatically.',
            fields: {
                participantId: {
                    type: 'uuid',
                    required: true,
                    pk: true,
                    default: '__UUID__',
                    comment: 'Unique id for this participant'
                },
                /**
                 * The participant in the matter.
                 */
                contactId: {
                    type: 'uuid',
                    entityType: 'contact',
                    required: true,
                    comment: 'The participant'
                },
                /**
                 * A lookup for relationships, like 'Brother-in-Law'.
                 */
                relationships: {
                    type: 'uuid[]',
                    entityType: 'lookup',
                    lookupPaths: ['any:participant:relationship'],
                    additionalData: {
                        type: {
                            type: 'string',
                            name: 'Type',
                            default: 'Familial'
                        },
                        opposite: {
                            type: 'array',
                            name: 'Opposite',
                            default: []
                        }
                    },
                    required: true,
                    comment: 'The relationships of this participant to the matter',
                },
                /**
                 * A short description of the participant, or a note on their involvement.
                 */
                description: {
                    type: 'string',
                    comment: 'A short description of the participant, or a note on their involvement'
                }
            },
            indexes: [{
                name: 'benefactors',
                fn: t => t.relationships.includes('73d54d07-2016-4a9e-9c89-ad843d93e178')
            }, ],
            maps: [{
                    name: 'entityId',
                    keysFn: t => t.entityId,
                    presentFn: (t, k) => t.entityId === k
                },
                {
                    name: 'contactId',
                    keysFn: t => t.contactId,
                    presentFn: (t, k) => t.contactId === k
                },
                {
                    name: 'relationships',
                    keysFn: t => t.relationships,
                    presentFn: (t, k) => t.relationships?.includes(k)
                },
            ]
        });
    }

}
export default Participant;
